<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  // Map of 'YYYY-MM-DD' -> array of events
  eventsByDate: { type: Object, default: () => ({}) },
});

const emit = defineEmits(['create', 'select']);

const { t, tm, locale } = useI18n();

const WEEKDAYS = computed(() => tm('CALENDAR.MONTH.WEEKDAYS'));

// First day of the month currently in view.
const cursor = ref(new Date(new Date().getFullYear(), new Date().getMonth(), 1));

const pad = n => String(n).padStart(2, '0');
const formatKey = d =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const todayKey = formatKey(new Date());

const monthLabel = computed(() => {
  const label = cursor.value.toLocaleDateString(locale.value, {
    month: 'long',
    year: 'numeric',
  });
  return label.charAt(0).toUpperCase() + label.slice(1);
});

// 6 weeks x 7 days grid, starting on Monday.
const weeks = computed(() => {
  const year = cursor.value.getFullYear();
  const month = cursor.value.getMonth();
  const monthStart = new Date(year, month, 1);
  const offset = (monthStart.getDay() + 6) % 7; // Monday = 0
  const gridStart = new Date(year, month, 1 - offset);

  const result = [];
  for (let w = 0; w < 6; w += 1) {
    const days = [];
    for (let d = 0; d < 7; d += 1) {
      const date = new Date(
        gridStart.getFullYear(),
        gridStart.getMonth(),
        gridStart.getDate() + (w * 7 + d)
      );
      const key = formatKey(date);
      days.push({
        key,
        day: date.getDate(),
        inMonth: date.getMonth() === month,
        isToday: key === todayKey,
        events: props.eventsByDate[key] || [],
      });
    }
    result.push(days);
  }
  return result;
});

const prevMonth = () => {
  cursor.value = new Date(
    cursor.value.getFullYear(),
    cursor.value.getMonth() - 1,
    1
  );
};
const nextMonth = () => {
  cursor.value = new Date(
    cursor.value.getFullYear(),
    cursor.value.getMonth() + 1,
    1
  );
};
const goToday = () => {
  cursor.value = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
};
</script>

<template>
  <div class="flex flex-col w-full h-full overflow-hidden">
    <!-- Toolbar -->
    <div class="flex items-center justify-between gap-3 mb-4 flex-shrink-0">
      <div class="flex items-center gap-2">
        <Button
          variant="outline"
          color="slate"
          size="sm"
          icon="i-lucide-chevron-left"
          @click="prevMonth"
        />
        <h2 class="text-base font-semibold text-n-slate-12 m-0 min-w-[10rem]">
          {{ monthLabel }}
        </h2>
        <Button
          variant="outline"
          color="slate"
          size="sm"
          icon="i-lucide-chevron-right"
          @click="nextMonth"
        />
        <Button
          :label="t('CALENDAR.MONTH.TODAY')"
          variant="ghost"
          color="slate"
          size="sm"
          @click="goToday"
        />
      </div>
    </div>

    <!-- Weekday header -->
    <div class="grid grid-cols-7 flex-shrink-0">
      <div
        v-for="wd in WEEKDAYS"
        :key="wd"
        class="px-2 py-1 text-xs font-medium text-n-slate-10 text-center"
      >
        {{ wd }}
      </div>
    </div>

    <!-- Day grid -->
    <div class="grid grid-cols-7 grid-rows-6 flex-1 gap-px bg-n-weak rounded-lg overflow-hidden border border-n-weak">
      <div
        v-for="cell in weeks.flat()"
        :key="cell.key"
        class="flex flex-col gap-1 p-1.5 min-h-0 overflow-hidden cursor-pointer transition-colors"
        :class="[
          cell.inMonth ? 'bg-n-solid-1 hover:bg-n-alpha-1' : 'bg-n-solid-2/50',
        ]"
        @click="emit('create', cell.key)"
      >
        <div class="flex items-center justify-between">
          <span
            class="text-xs font-medium grid place-content-center size-5 rounded-full"
            :class="[
              cell.isToday
                ? 'bg-n-brand text-white'
                : cell.inMonth
                  ? 'text-n-slate-11'
                  : 'text-n-slate-9',
            ]"
          >
            {{ cell.day }}
          </span>
        </div>
        <div class="flex flex-col gap-1 overflow-y-auto no-scrollbar">
          <button
            v-for="event in cell.events"
            :key="event.id"
            type="button"
            class="flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] leading-tight text-left truncate bg-n-solid-3 hover:bg-n-solid-active text-n-slate-12"
            @click.stop="emit('select', event)"
          >
            <span
              class="size-1.5 rounded-full flex-shrink-0"
              :style="{ backgroundColor: event.color || '#3b82f6' }"
            />
            <span v-if="event.startTime" class="text-n-slate-10 flex-shrink-0">
              {{ event.startTime }}
            </span>
            <span class="truncate">{{ event.title }}</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
